document.addEventListener('DOMContentLoaded', e => {
  const scrollContainerSelector_ = '[data-cta-middle-scroll-container]';
  const scrollCtaSelector_ = '[data-cta-middle-scroll]';
  const scrollCtaContainerEls_ = Array.from(document.querySelectorAll(scrollContainerSelector_));
  let ticking = false;

  // Check if shop is opened from instagram browser on ios device
  const stickyTitleEnabled_ = !(navigator.userAgent.indexOf("Instagram") != -1 && navigator.userAgent.indexOf("iOS") != -1);

  /**
   * @typedef {Object} ContainerState
   * @property {number} containerWidth - The width of the container, in pixels
   * @property {number} containerDistanceFromLeft - The distance from the left of the viewport, in pixels
   * @property {number} spacing - The distance from the left of the viewport, in pixels
   */

  /**
   *
   * @param {HTMLElement} containerEl - The main container
   *
   * @returns {ContainerState}
   */
  const getContainerState_ = (containerEl) => {
    const spacing = JSON.parse(containerEl.dataset.ctaScrollSpacing);
    let useSpacing;

    if (matchMedia('(max-width: 767px)').matches) {
      useSpacing = spacing.mobile;
    } else {
      useSpacing = spacing.desktop;
    }

    return {
      containerWidth: containerEl.getBoundingClientRect().width,
      containerDistanceFromLeft: containerEl.getBoundingClientRect().x,
      spacing: useSpacing,
    }
  };

  /**
   *
   * @param {HTMLElement} scrollCtaEl - The element that moves with scrolling
   * @param {ContainerState} containerState
   */
  const setCtaState_ = (scrollCtaEl, containerState = {}) => {
    const { spacing, containerWidth, containerDistanceFromLeft } = containerState;
    const width = containerWidth - spacing * 2;
    const distanceFromLeft = containerDistanceFromLeft + spacing;

    scrollCtaEl.style.width = `${width}px`;
    scrollCtaEl.style.setProperty('--spacing', `${spacing}px`);
    scrollCtaEl.style.setProperty('--distance-from-left', `${distanceFromLeft}px`);
  }

  const positionCta_ = (scrollCtaContainer, scrollState) => {
    const clientRect = scrollCtaContainer.getBoundingClientRect();
    const cta = scrollCtaContainer.querySelector(scrollCtaSelector_);
    const { isScrollingDown, isScrollingUp } = scrollState;

    if (cta && stickyTitleEnabled_) {
      const ctaClientRect = cta.getBoundingClientRect();
      const spacing = parseInt(cta.style.getPropertyValue('--spacing').replace('px', ''));
      const distanceFromLeft = parseInt(cta.style.getPropertyValue('--distance-from-left').replace('px', ''));
      const translateY = window.innerHeight - clientRect.y - clientRect.height;

        const viewportBottom = window.innerHeight;
        const viewportTop = 0;
        const containerTop = clientRect.y;
        const containerBottom = clientRect.y + clientRect.height;
        const containerHeight = clientRect.height;

        const contentCenteredY = containerTop + containerHeight / 2;

        const containerOutOfView = containerBottom < 0 || containerTop > viewportBottom;

        if (containerOutOfView) {
          // Container out of view - returning
          return;
        }

        const centerOffset = Math.max((containerHeight - ctaClientRect.height) / 2, 0);

        const applyFixedAtBottom = () => {
          cta.style.setProperty('position', 'fixed', 'important');
          cta.style.setProperty('top', 'unset', 'important');
          cta.style.setProperty('bottom', `${spacing}px`, 'important');
          cta.style.setProperty('left', `${distanceFromLeft}px`, 'important');
          cta.style.setProperty('transform', 'none', 'important');
        };

        const applyAbsoluteTop = () => {
          cta.style.setProperty('position', 'absolute', 'important');
          cta.style.setProperty('top', `${spacing}px`, 'important');
          cta.style.setProperty('bottom', 'unset', 'important');
          cta.style.setProperty('left', `${distanceFromLeft}px`, 'important');
          cta.style.setProperty('transform', 'none', 'important');
        };

        const applyAbsoluteCenter = () => {
          cta.style.setProperty('position', 'absolute', 'important');
          cta.style.setProperty('top', `50%`, 'important');
          cta.style.setProperty('bottom', 'unset', 'important');
          cta.style.setProperty('left', `${distanceFromLeft}px`, 'important');
          cta.style.setProperty('transform', 'translateY(-50%)', 'important');
        };

        // Calculate where the centered text bottom would be in viewport
        const centeredTextTop = containerTop + centerOffset;
        const centeredTextBottom = centeredTextTop + ctaClientRect.height;

        // Calculate where the text would be if fixed at bottom of viewport
        const fixedBottomPosition = viewportBottom - spacing;
        const fixedTopPosition = fixedBottomPosition - ctaClientRect.height;

        // Check if centered text has hit or passed the bottom viewport
        const centeredTextBelowViewport = centeredTextBottom > viewportBottom - spacing;

        // Check if the fixed position would place text above the container top (going out of bounds)
        const wouldGoAboveContainer = fixedTopPosition < containerTop + spacing;

        if (centeredTextBelowViewport) {
          // Text needs to stick to bottom of viewport and move with scroll
          if (wouldGoAboveContainer) {
            // Would go above container - stop at container top
            applyAbsoluteTop();
          } else {
            // Stick to viewport bottom
            applyFixedAtBottom();
          }
        } else {
          // Text stays centered in container
          applyAbsoluteCenter();
        }

    }
  }

  scrollCtaContainerEls_.forEach(containerEl => {
    const { containerWidth, containerDistanceFromLeft, spacing } = getContainerState_(containerEl);
    const scrollCtaEl = containerEl.querySelector(scrollCtaSelector_);

    setCtaState_(scrollCtaEl, {
      containerWidth,
      containerDistanceFromLeft,
      spacing
    });

    // Initialize CTA at center position
    positionCta_(containerEl, { isScrollingDown: false, isScrollingUp: false });
  });

  const resizeObserver_ = new ResizeObserver((entries) => {
    for (let entry of entries) {
      const containerEl = entry.target;
      const { containerWidth, containerDistanceFromLeft, spacing } = getContainerState_(containerEl);
      const scrollCtaEl = containerEl.querySelector(scrollCtaSelector_);

      setCtaState_(scrollCtaEl, {
        containerWidth,
        containerDistanceFromLeft,
        spacing
      });

      positionCta_(containerEl, { isScrollingDown: false, isScrollingUp: false });
    }
  });
  let lastScrollY = window.scrollY;

  scrollCtaContainerEls_.forEach(containerEl => resizeObserver_.observe(containerEl));

  const updatePositions = () => {
    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > lastScrollY;
    const isScrollingUp = currentScrollY < lastScrollY;

    const scrollState = {
      isScrollingDown,
      isScrollingUp,
    };

    scrollCtaContainerEls_.forEach(scrollCtaContainer => positionCta_(scrollCtaContainer, scrollState));

    lastScrollY = currentScrollY <= 0 ? 0 : currentScrollY;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      updatePositions();
      ticking = false;
    });
  };

  document.addEventListener('scroll', onScroll);
  window.requestAnimationFrame(updatePositions);
});
